<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const dialogRef = ref(null);
const targetStage = ref(null);

const form = reactive({ name: '', amount: '', expectedCloseOn: '' });

const isFormInvalid = computed(() => !form.name.trim());

const resetForm = () => {
  form.name = '';
  form.amount = '';
  form.expectedCloseOn = '';
  targetStage.value = null;
};

const open = stage => {
  resetForm();
  targetStage.value = stage;
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;

  emit('create', {
    name: form.name.trim(),
    pipelineStageId: targetStage.value?.id,
    amountCents: Math.round(Number(form.amount || 0) * 100),
    expectedCloseOn: form.expectedCloseOn || null,
  });
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const onSuccess = () => {
  resetForm();
  closeDialog();
};

defineExpose({ dialogRef, onSuccess, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-6">
      <div class="flex flex-col items-start gap-2">
        <span class="py-1 text-sm font-medium text-n-slate-12">
          {{ t('DEALS.CREATE.TITLE') }}
        </span>
        <Input
          v-model="form.name"
          :placeholder="t('DEALS.CREATE.FIELDS.NAME')"
          :disabled="isLoading"
          class="w-full"
          autofocus
        />
        <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="form.amount"
            type="number"
            min="0"
            :placeholder="t('DEALS.CREATE.FIELDS.AMOUNT')"
            :disabled="isLoading"
          />
          <Input
            v-model="form.expectedCloseOn"
            type="date"
            :placeholder="t('DEALS.CREATE.FIELDS.EXPECTED_CLOSE_ON')"
            :disabled="isLoading"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          :label="t('DEALS.CREATE.ACTIONS.SAVE')"
          color="blue"
          type="submit"
          :disabled="isFormInvalid || isLoading"
          :is-loading="isLoading"
        />
      </div>
    </template>
  </Dialog>
</template>
